import { Routes } from '@angular/router';
import { eventAuthGuard } from 'src/app/@core/auth/guards/event-auth-guard';
import { loginRedirectGuard } from 'src/app/@core/auth/guards/login-redirect-guard';
import { determineEventRegistrationId } from 'src/app/@guards/determine-event-registration-id';

/** Routes as `event/:eventName/...` (direct event hostname / child CloudFront). */
const eventPrefixedRoutes: Routes = [
  {
    path: 'event/:eventName',
    loadComponent: () =>
      import('./@pages/reports-home/reports-home.component').then(
        (c) => c.ReportsHomeComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/debriefs/view-report',
    loadComponent: () =>
      import('./@pages/view-report/view-report.component').then(
        (c) => c.ViewReportComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/blocked',
    loadComponent: () =>
      import('./@pages/auth-blocked/auth-blocked.component').then(
        (c) => c.AuthBlockedComponent
      ),
  },
  {
    path: 'event/:eventName/debriefs',
    loadComponent: () =>
      import('./@pages/reports-debriefs/reports-debriefs.component').then(
        (c) => c.ReportsDebriefsComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/hub/session/:sessionId',
    loadComponent: () =>
      import('./@pages/hub-public/hub-session-detail/hub-session-detail.component').then(
        (c) => c.HubSessionDetailComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/hub/asset/:assetId',
    loadComponent: () =>
      import('./@pages/hub-public/hub-asset-detail/hub-asset-detail.component').then(
        (c) => c.HubAssetDetailComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/hub',
    loadComponent: () =>
      import('./@pages/hub-public/hub-library/hub-library.component').then(
        (c) => c.HubLibraryComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/debriefs/view-report',
    loadComponent: () =>
      import('./@pages/view-report/view-report.component').then(
        (c) => c.ViewReportComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/hub/session/:sessionId',
    loadComponent: () =>
      import('./@pages/hub-public/hub-session-detail/hub-session-detail.component').then(
        (c) => c.HubSessionDetailComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/hub/asset/:assetId',
    loadComponent: () =>
      import('./@pages/hub-public/hub-asset-detail/hub-asset-detail.component').then(
        (c) => c.HubAssetDetailComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/hub',
    loadComponent: () =>
      import('./@pages/hub-public/hub-library/hub-library.component').then(
        (c) => c.HubLibraryComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/debriefs',
    loadComponent: () =>
      import('./@pages/reports-debriefs/reports-debriefs.component').then(
        (c) => c.ReportsDebriefsComponent
      ),
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId',
    loadComponent: () =>
      import('./@pages/reports-home-intermediate/reports-home-intermediate.component').then(
        (c) => c.ReportsHomeIntermediateComponent
      ),
    resolve: { state: determineEventRegistrationId },
    canActivate: [eventAuthGuard],
  },
  {
    path: 'event/:eventName/:registrationId/view-report',
    loadComponent: () =>
      import('./@pages/view-report-intermediate/view-report-intermediate.component').then(
        (c) => c.ViewReportIntermediateComponent
      ),
    resolve: { state: determineEventRegistrationId },
    canActivate: [eventAuthGuard],
  },
];

/**
 * Legacy short paths (`/reports/:eventName/...`) redirect to canonical
 * `/reports/event/:eventName/...` on the parent hub.
 */
const reportsStrippedPrefixRoutes: Routes = [
  {
    path: ':eventName/debriefs/view-report',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/debriefs/view-report`,
  },
  {
    path: ':eventName/blocked',
    redirectTo: (route) => `event/${route.params['eventName']}/blocked`,
  },
  {
    path: ':eventName/debriefs',
    redirectTo: (route) => `event/${route.params['eventName']}/debriefs`,
  },
  {
    path: ':eventName/hub/session/:sessionId',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/hub/session/${route.params['sessionId']}`,
  },
  {
    path: ':eventName/hub/asset/:assetId',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/hub/asset/${route.params['assetId']}`,
  },
  {
    path: ':eventName/hub',
    redirectTo: (route) => `event/${route.params['eventName']}/hub`,
  },
  {
    path: ':eventName/:registrationId/hub/session/:sessionId',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/hub/session/${route.params['sessionId']}`,
  },
  {
    path: ':eventName/:registrationId/hub/asset/:assetId',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/hub/asset/${route.params['assetId']}`,
  },
  {
    path: ':eventName/:registrationId/hub',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/hub`,
  },
  {
    path: ':eventName/:registrationId/debriefs/view-report',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/debriefs/view-report`,
  },
  {
    path: ':eventName/:registrationId/debriefs',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/debriefs`,
  },
  {
    path: ':eventName/:registrationId/view-report',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}/view-report`,
  },
  {
    path: ':eventName/:registrationId',
    redirectTo: (route) =>
      `event/${route.params['eventName']}/${route.params['registrationId']}`,
  },
  {
    path: ':eventName',
    redirectTo: (route) => `event/${route.params['eventName']}`,
  },
];

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () =>
      import('./@core/auth/components/login/login.component').then(
        (c) => c.LoginComponent
      ),
    canActivate: [loginRedirectGuard],
  },
  {
    path: 'otp',
    loadComponent: () =>
      import('./@core/auth/components/otp/otp.component').then(
        (c) => c.OtpComponent
      ),
    canActivate: [loginRedirectGuard],
  },
  ...eventPrefixedRoutes,
  ...reportsStrippedPrefixRoutes,
  {
    path: '**',
    loadComponent: () =>
      import('./@pages/page-not-found/page-not-found.component').then(
        (c) => c.PageNotFoundComponent
      ),
  },
];
